import { useEffect, useMemo, useRef } from 'react';
import type { ApiClient } from '../api/client.js';
import { detectDevice } from '../lib/detectDevice.js';
import type { TrackingBuffer } from '../lib/trackingBuffer.js';

export interface UseAnalyticsResult {
  sessionId: string | null;
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(8));
    const random = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `analytics-${Date.now()}-${random}`;
  }
  return `analytics-${Date.now()}`;
}

export function useAnalytics(
  userId: string | undefined,
  api: ApiClient,
  buffer: TrackingBuffer,
): UseAnalyticsResult {
  const sessionId = useMemo(() => (userId ? newSessionId() : null), [userId]);
  const startedAtRef = useRef<number>(Date.now());
  const buttonClicksRef = useRef(0);
  const finalisedRef = useRef(false);

  useEffect(() => {
    if (!userId || !sessionId) return;
    startedAtRef.current = Date.now();
    finalisedRef.current = false;
    buffer.enqueue({
      type: 'track-analytics-session',
      payload: {
        sessionId,
        sessionStart: new Date(startedAtRef.current).toISOString(),
      },
    });
    buffer.enqueue({ type: 'track-ip', payload: { sessionId } });
    void detectDevice().then((device) => {
      buffer.enqueue({
        type: 'track-device',
        payload: {
          sessionId,
          fingerprint: device.fingerprint,
          deviceType: device.deviceType,
          deviceModel: device.deviceModel,
          platform: device.platform,
          userAgent: device.userAgent,
          screenWidth: device.screenWidth,
          screenHeight: device.screenHeight,
        },
      });
      buffer.enqueue({
        type: 'track-analytics-session',
        payload: {
          sessionId,
          sessionStart: new Date(startedAtRef.current).toISOString(),
          deviceType: device.deviceType,
        },
      });
    });

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button, [role="button"], a, input[type="button"], input[type="submit"]')) {
        buttonClicksRef.current += 1;
      }
    };

    const sendHeartbeat = () => {
      buffer.enqueue({
        type: 'track-analytics-heartbeat',
        payload: {
          sessionId,
          sessionDurationSeconds: Math.round((Date.now() - startedAtRef.current) / 1000),
          buttonClicks: buttonClicksRef.current,
        },
      });
    };

    const finalise = () => {
      if (finalisedRef.current) return;
      finalisedRef.current = true;
      const finishedAt = new Date().toISOString();
      const sessionDurationSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
      buffer.enqueue({
        type: 'track-analytics-finalise',
        payload: {
          sessionId,
          sessionEnd: finishedAt,
          sessionDurationSeconds,
          buttonClicks: buttonClicksRef.current,
        },
      });
      buffer.enqueue({
        type: 'track-analytics-summary',
        payload: {
          sessionId,
          deltaSessions: 1,
          deltaTimeSeconds: sessionDurationSeconds,
          deltaButtonClicks: buttonClicksRef.current,
          lastSessionAt: finishedAt,
        },
      });
      void buffer.flush({ keepalive: true });
    };

    const visibilityHandler = () => {
      if (document.visibilityState === 'hidden') finalise();
    };

    document.addEventListener('click', clickHandler, true);
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('beforeunload', finalise);
    window.addEventListener('pagehide', finalise);
    const heartbeatTimer = window.setInterval(sendHeartbeat, 30_000);

    return () => {
      document.removeEventListener('click', clickHandler, true);
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('beforeunload', finalise);
      window.removeEventListener('pagehide', finalise);
      window.clearInterval(heartbeatTimer);
    };
  }, [api, buffer, sessionId, userId]);

  return { sessionId };
}
